"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.selectThreeMonthsDailyAverage = exports.selectMovingCalendarMinMax = exports.selectEmptyCalendarData = exports.selectCalendarData = void 0;
const toolkit_1 = require("@reduxjs/toolkit");
const lodash_1 = require("lodash");
const moment_1 = __importDefault(require("moment"));
const dateFormat_1 = require("../types/dateFormat");
const WEEKDAYS_COUNT = 7;
const getMonthWeekBoundariesForDate = (date) => {
    const year = date.year();
    const month = date.month();
    const firstDayOfMonthWeek = (0, moment_1.default)([year, month])
        .startOf("month")
        .startOf("week");
    const lastDayOfMonthWeek = (0, moment_1.default)([year, month]).endOf("month").endOf("week");
    return { firstDayOfMonthWeek, lastDayOfMonthWeek };
};
const prepareCalendarDataCell = (date, value) => {
    return {
        date: date.format("YYYY-MM-DD"),
        dayNumber: date.format("D"),
        value,
    };
};
const getValueForDate = (date, streamDailyAverages) => {
    const dailyAverage = streamDailyAverages.find((item) => item.date === date);
    return dailyAverage ? dailyAverage.value : null;
};
const getMonthWeeksOfDailyAveragesFor = (0, lodash_1.memoize)((month, streamDailyAverages) => {
    if (!month || !month.isValid() || !streamDailyAverages) {
        throw new Error("Invalid inputs");
    }
    const { firstDayOfMonthWeek, lastDayOfMonthWeek } = getMonthWeekBoundariesForDate(month);
    let currentDate = firstDayOfMonthWeek.clone();
    const weeks = [];
    while (currentDate <= lastDayOfMonthWeek) {
        const week = [];
        for (let i = 0; i < WEEKDAYS_COUNT; i++) {
            const isCurrentMonth = currentDate.isSame(month, "month");
            const value = isCurrentMonth
                ? getValueForDate(currentDate.format("YYYY-MM-DD"), streamDailyAverages)
                : null;
            week.push(prepareCalendarDataCell(currentDate.clone(), value));
            currentDate.add(1, "day");
        }
        weeks.push(week);
    }
    const dayNamesHeader = weeks[0].map((day) => (0, moment_1.default)(day.date).format("dddd").substring(0, 3));
    return {
        monthName: month.format("MMMM"),
        dayNamesHeader,
        weeks,
    };
}, (month, streamDailyAverages) => `${month.format("YYYY-MM")}-${JSON.stringify(streamDailyAverages)}`);
const getVisibleMonthsData = (streamDailyAverages, startDate, endDate) => {
    if (!streamDailyAverages || !startDate || !endDate)
        return [];
    const startMoment = (0, moment_1.default)(startDate, dateFormat_1.DateFormat.us).startOf("day");
    const endMoment = (0, moment_1.default)(endDate, dateFormat_1.DateFormat.us).endOf("day");
    return streamDailyAverages.filter((average) => {
        const dateMoment = (0, moment_1.default)(average.date, dateFormat_1.DateFormat.default);
        return dateMoment.isBetween(startMoment, endMoment, "day", "[]");
    });
};
const _selectMovingCalendarData = (state) => {
    return state.movingCalendarStream.data;
};
const selectThreeMonthsDailyAverage = (0, toolkit_1.createSelector)([
    _selectMovingCalendarData,
    (_state, startDate, endDate) => ({
        startDate,
        endDate,
    }),
], (calendarData, { startDate, endDate }) => {
    const emptyResult = [];
    if (!(calendarData === null || calendarData === void 0 ? void 0 : calendarData.length) || !startDate || !endDate) {
        return emptyResult;
    }
    const endMoment = (0, moment_1.default)(endDate, dateFormat_1.DateFormat.us);
    if (!endMoment.isValid()) {
        return emptyResult;
    }
    const visibleData = getVisibleMonthsData(calendarData, startDate, endDate);
    const latestMomentWithData = endMoment.clone().endOf("month");
    const secondLatestMonth = latestMomentWithData.clone().subtract(1, "month");
    const thirdLatestMonth = latestMomentWithData.clone().subtract(2, "month");
    if (!latestMomentWithData.isValid() ||
        !secondLatestMonth.isValid() ||
        !thirdLatestMonth.isValid()) {
        return emptyResult;
    }
    const threeMonths = [
        thirdLatestMonth,
        secondLatestMonth,
        latestMomentWithData,
    ];
    try {
        return threeMonths.map((month) => getMonthWeeksOfDailyAveragesFor(month, visibleData));
    }
    catch (error) {
        console.error("Error generating calendar data:", error);
        return emptyResult;
    }
});
exports.selectThreeMonthsDailyAverage = selectThreeMonthsDailyAverage;
const selectMovingCalendarMinMax = (0, toolkit_1.createSelector)(_selectMovingCalendarData, (calendarData) => {
    if (!calendarData || calendarData.length === 0) {
        return { min: null, max: null };
    }
    const values = calendarData.map((entry) => entry.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    return { min, max };
});
exports.selectMovingCalendarMinMax = selectMovingCalendarMinMax;
const selectEmptyCalendarData = (0, toolkit_1.createSelector)([_selectMovingCalendarData], (calendarData) => {
    const emptyResult = [];
    if (!(calendarData === null || calendarData === void 0 ? void 0 : calendarData.length)) {
        return emptyResult;
    }
    // Use current date for empty calendar
    const now = (0, moment_1.default)();
    const endDate = now.format(dateFormat_1.DateFormat.us);
    const startDate = now.clone().subtract(3, "months").format(dateFormat_1.DateFormat.us);
    const visibleData = getVisibleMonthsData(calendarData, startDate, endDate);
    const latestMonth = now.clone().startOf("month");
    const secondMonth = latestMonth.clone().subtract(1, "month");
    const thirdMonth = latestMonth.clone().subtract(2, "month");
    const threeMonths = [thirdMonth, secondMonth, latestMonth];
    try {
        return threeMonths.map((month) => getMonthWeeksOfDailyAveragesFor(month, visibleData));
    }
    catch (error) {
        console.error("Error generating calendar data:", error);
        return emptyResult;
    }
});
exports.selectEmptyCalendarData = selectEmptyCalendarData;
const selectCalendarData = (0, toolkit_1.createSelector)([
    _selectMovingCalendarData,
    (_state, startDate, endDate) => startDate && endDate ? `${startDate}-${endDate}` : "",
], (calendarData, dateKey) => {
    const emptyResult = [];
    if (!(calendarData === null || calendarData === void 0 ? void 0 : calendarData.length) || !dateKey) {
        return emptyResult;
    }
    const [startDate, endDate] = dateKey.split("-");
    if (!startDate || !endDate) {
        return emptyResult;
    }
    const endMoment = (0, moment_1.default)(endDate, dateFormat_1.DateFormat.us);
    if (!endMoment.isValid()) {
        return emptyResult;
    }
    const visibleData = getVisibleMonthsData(calendarData, startDate, endDate);
    const latestMomentWithData = endMoment.clone().endOf("month");
    const secondLatestMonth = latestMomentWithData.clone().subtract(1, "month");
    const thirdLatestMonth = latestMomentWithData.clone().subtract(2, "month");
    if (!latestMomentWithData.isValid() ||
        !secondLatestMonth.isValid() ||
        !thirdLatestMonth.isValid()) {
        return emptyResult;
    }
    const threeMonths = [
        thirdLatestMonth,
        secondLatestMonth,
        latestMomentWithData,
    ];
    try {
        return threeMonths.map((month) => getMonthWeeksOfDailyAveragesFor(month, visibleData));
    }
    catch (error) {
        console.error("Error generating calendar data:", error);
        return emptyResult;
    }
});
exports.selectCalendarData = selectCalendarData;
